/**
 * @module monetization/useBanners
 *
 * Hand-written React Query hooks for the ARC-112 monetization **banner** slot
 * endpoints. These are NOT yet part of the regenerated Orval client, so — like
 * the rest of `monetization/api.ts` — they mirror the generated client's shape
 * by hand: a `use*Hook` that wraps the request in a `useCallback`-stable
 * function via {@link useCustomInstance} (which forwards the player JWT when
 * one exists), a `get*QueryKey` helper, and a public hook over `useQuery`.
 *
 * Two surfaces are served:
 *  - **LANDER** (`useGuestBanners`) — the logged-out landing page. Backed by the
 *    PUBLIC `GET /banners` endpoint; the visitor has no session, so
 *    the request carries no auth token ({@link useCustomInstance} sends none when
 *    logged out — `getAuthHeaders()` returns `{}`), and it must still succeed.
 *  - **LOBBY** (`useLobbyBanners`) — the signed-in lobby. Backed by the authed
 *    `GET /banners/lobby` endpoint.
 *
 * @see src/lib/use-custom-instance.ts — the auth-forwarding axios mutator.
 * @see src/lib/monetization/api.ts — the sibling hand-written monetization hooks.
 */
import { useCallback } from 'react';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';

/** The surface a banner is eligible for. `BOTH` = lander and lobby. */
export type BannerSurface = 'LANDER' | 'LOBBY' | 'BOTH';

/** Where a banner CTA sends the player. */
export type BannerTargetKind = 'CATEGORY' | 'SUBCATEGORY' | 'EVENT';

export interface BannerCtaTarget {
  kind: BannerTargetKind;
  id: string;
}

export interface BannerCta {
  label: string;
  target: BannerCtaTarget;
}

/**
 * A single banner as returned by the monetization banner endpoints. Kept local
 * (next to the hooks) because the banner endpoints are not part of the
 * regenerated Orval client. Mirrors the api `SurfaceBanner` shape.
 */
export interface SurfaceBanner {
  imageUrl?: string;
  cta?: BannerCta;
  surface: BannerSurface;
  /** Server-assigned display order within the slot (lower first). */
  slot: number;
  /** Extra query param the lander should echo back to the api (pass-through). */
  landerQueryParam?: string;
}

/** `{ banners }` envelope both endpoints return. */
export interface BannersResponse {
  banners: SurfaceBanner[];
}

/** The surfaces `useGuestBanners` may request (never `BOTH` — that's a stored eligibility, not a query). */
export type GuestBannerSurface = 'LANDER' | 'LOBBY';

/* ── Guest / public banners (lander) ───────────────────────────────── */

export const getGuestBannersQueryKey = (surface: GuestBannerSurface, queryParams?: Record<string, string>) =>
  ['/banners', surface, queryParams ?? {}] as const;

const useGuestBannersHook = (surface: GuestBannerSurface, queryParams?: Record<string, string>) => {
  const request = useCustomInstance<BannersResponse>();
  return useCallback(
    (signal?: AbortSignal) =>
      request({
        url: '/banners',
        method: 'GET',
        params: { surface, ...queryParams },
        signal,
      }),
    [request, surface, queryParams],
  );
};

/**
 * Banners for a public (logged-out) surface — the lander. Public endpoint, so
 * it works with no user/token. `queryParams` are forwarded as-is to the api
 * (pass-through targeting, e.g. campaign params off the current URL).
 */
export function useGuestBanners<TData = BannersResponse, TError = unknown>(
  surface: GuestBannerSurface,
  queryParams?: Record<string, string>,
  options?: { query?: Partial<UseQueryOptions<BannersResponse, TError, TData>> },
): UseQueryResult<TData, TError> {
  const guestBanners = useGuestBannersHook(surface, queryParams);
  const { query } = options ?? {};
  return useQuery({
    queryKey: getGuestBannersQueryKey(surface, queryParams),
    queryFn: ({ signal }) => guestBanners(signal),
    // Banners change on an Ops cadence, not per-interaction — cache 5 min.
    staleTime: 5 * 60_000,
    // A missing/erroring banner slot must never storm the api (and must never
    // break the lander) — one retry, then fall back to "no banners".
    retry: 1,
    ...query,
  });
}

/* ── Lobby banners (authed) ────────────────────────────────────────── */

export const getLobbyBannersQueryKey = () => ['/banners/lobby'] as const;

const useLobbyBannersHook = () => {
  const request = useCustomInstance<BannersResponse>();
  return useCallback((signal?: AbortSignal) => request({ url: '/banners/lobby', method: 'GET', signal }), [request]);
};

/** Banners for the signed-in lobby. Authed endpoint — the player JWT is forwarded automatically. */
export function useLobbyBanners<TData = BannersResponse, TError = unknown>(options?: {
  query?: Partial<UseQueryOptions<BannersResponse, TError, TData>>;
}): UseQueryResult<TData, TError> {
  const lobbyBanners = useLobbyBannersHook();
  const { query } = options ?? {};
  return useQuery({
    queryKey: getLobbyBannersQueryKey(),
    queryFn: ({ signal }) => lobbyBanners(signal),
    staleTime: 5 * 60_000,
    retry: 1,
    ...query,
  });
}
